import { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import { formatPercent } from '@/lib/format-utils'
import { exportToCsv, cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DropdownMediaGroup } from '@/components/DropdownMediaGroup'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ChevronLeftIcon, CalendarIcon } from '@radix-ui/react-icons'
import { format } from 'date-fns'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import dayjs from 'dayjs'
import { ROAS_STANDARD_DAY_OPTIONS } from '@/features/roas/constants/roas-days'

import SingleDatePicker from './components/report-single-date-picker'

const DetailReport = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const pid = searchParams.get('pid')
  const country = searchParams.get('country')
  const initialDate = searchParams.get('date')
  const initialDay = searchParams.get('day') || '0'
  const initialMediaSource = searchParams.get('mediaSource') || 'app'

  const [date, setDate] = useState(initialDate)
  const [selectedDay, setSelectedDay] = useState(initialDay)
  const [mediaSource, setMediaSource] = useState(initialMediaSource)

  // Sync state to URL params
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        if (
          date !== newParams.get('date') ||
          selectedDay !== newParams.get('day') ||
          mediaSource !== newParams.get('mediaSource')
        ) {
          newParams.set('date', date)
          newParams.set('day', selectedDay)
          newParams.set('mediaSource', mediaSource)
          return newParams
        }
        return prev
      },
      { replace: true },
    )
  }, [date, selectedDay, mediaSource, setSearchParams])

  const dayValue = Number(selectedDay)

  const {
    data: response,
    isLoading,
    error,
    mutate,
    isValidating,
  } = useSWR([
    `/troas/standard-report`,
    { 
      startDate: date, 
      endDate: date, 
      mediaSource,
      day_n: dayValue,
      pid: pid,
      country: country,
      mode: 'package'
    },
  ])

  const reportData = response?.data || []

  // Filter data for the specific PID and Country
  const filteredData = useMemo(() => {
    return reportData.filter(
      (item) => item.pid === Number(pid) && item.country === country
    )
  }, [reportData, pid, country])

  // Standards description text with colored keywords
  const standardDescription = (
    <>
      <span className="text-green-600 font-bold">绿色：</span>达到或超过标准；
      <span className="text-yellow-600 font-bold">黄色：</span>低于标准2%以内；
      <span className="text-red-700 font-bold">红色：</span>低于标准2%以上；
      <span className="text-black font-bold">黑色：</span>未设定标准
    </>
  )

  const columns = useMemo(() => [packageColumn, metricsColumn], [])

  const handleExportCsv = () => {
    if (!filteredData.length) return

    const filename = `detail_report_pid${pid}_${country}_${date}_day${dayValue}_${dayjs().format(
      'YYYYMMDDHHmmss',
    )}.csv`
    const headers = ['Package Name', 'ROAS', '标准 ROAS', 'ROAS Diff']

    const rows = filteredData.map((item) => {
      const { actual, standard } = item
      const diff = actual - standard
      const diffStr = (diff > 0 ? '+' : '') + formatPercent(diff, 1)
      const targetStr = standard && standard > 0 ? formatPercent(standard, 1) : '-'
      const actualStr = formatPercent(actual, 1)

      return {
        'Package Name': item.packageName || 'Unknown',
        ROAS: actualStr,
        '标准 ROAS': targetStr,
        'ROAS Diff': diffStr,
      }
    })

    exportToCsv(rows, filename, headers)
  }

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">
          Detail Report: PID {pid} - {country}
        </h2>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4 flex-wrap sm:gap-4 sm:py-2">
        <SingleDatePicker dateStr={date} onDateChange={setDate} />
        <DropdownMediaGroup value={mediaSource} update={setMediaSource} />
        <Select value={selectedDay} onValueChange={setSelectedDay}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Day 0" />
          </SelectTrigger>
          <SelectContent>
            {ROAS_STANDARD_DAY_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => mutate()}
          disabled={isValidating}
        >
          Refresh {isValidating && '...'}
        </Button>
        <Button variant="outline" onClick={handleExportCsv}>
          Export
        </Button>
      </div>

      {/* Standards Description */}
      <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
        <span className="font-semibold">标准说明：</span>{' '}
        {standardDescription}
      </div>

      <div className="relative min-h-[256px]">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center text-lg font-medium">
            Loading Detail Report...
          </div>
        ) : error ? (
          <div className="flex h-64 items-center justify-center text-lg font-medium text-red-600">
            Failed to load data: {error?.message}
          </div>
        ) : (
          <DataTableWithBorder table={table} />
        )}
      </div>
    </div>
  )
}

export default DetailReport
